import { SentimentName } from 'v2/core/entities/fields';
import {
  OperatorExactlyAllOf,
  OperatorExcludeAllOf,
  OperatorExcludeAnyOf,
  OperatorForAllOf,
  OperatorHasAll,
  OperatorIsAllOf,
  OperatorIsNotAnyOf,
  OperatorIsNotPresent,
  OperatorIsPresent,
  OperatorNot,
  OperatorNotEquals,
  OperatorSentimentNegative,
  OperatorSentimentNeutral,
  OperatorSentimentPositive,
} from 'v2/core/services/queryBuilder/operators';
import {
  MetadataFilter,
  OperationType,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';
import checkIsExactSearchOperator from 'v2/core/utils/checkIsExactSearchOperator';
import Conjunction from 'v2/querybuilder/types/conjunction';
import FilterValue, {
  StringArrayFilterValue,
} from 'v2/querybuilder/types/filtervalue';
import { QBFilter, QBNode } from 'v2/querybuilder/types/qbNode';
import { decodeSourceFromString } from './utils';

const invalidFilterError = (
  field: string,
  single: boolean,
  valueType: string,
) =>
  Error(
    `invalid ${field} filter: expecting ${
      single ? 'single' : 'multiple'
    } ${valueType} value`,
  );

const toOperationTypeFromConjunction = (
  conjunction: Conjunction,
): OperationType => {
  switch (conjunction) {
    case 'AND':
      return OperationType.AND;
    case 'OR':
      return OperationType.OR;
  }
};

const toOperationTypeFromOperator = (operator: string): OperationType => {
  switch (operator) {
    case OperatorHasAll:
    case OperatorForAllOf:
    case OperatorExcludeAllOf:
    case OperatorIsAllOf:
    case OperatorExactlyAllOf:
      return OperationType.AND;
    default:
      return OperationType.OR;
  }
};

const isExclude = (operator: string): boolean => {
  switch (operator) {
    case OperatorNot:
    case OperatorNotEquals:
    case OperatorIsNotPresent:
    case OperatorIsNotAnyOf:
    case OperatorExcludeAnyOf:
    case OperatorExcludeAllOf:
      return true;
    default:
      return false;
  }
};

const toSentiment = (operator: string): SentimentName => {
  switch (operator) {
    case OperatorSentimentPositive:
      return SentimentName.POSITIVE;
    case OperatorSentimentNeutral:
      return SentimentName.NEUTRAL;
    case OperatorSentimentNegative:
      return SentimentName.NEGATIVE;
  }
  throw Error(`invalid sentiment: ${operator}`);
};

const toMetadataFilter = (
  key: string,
  value: FilterValue,
  operator: string,
): MetadataFilter => {
  switch (value.type) {
    case 'string[]':
      return {
        key,
        type: 'string',
        values: value.values.map((value) => value.value),
        operator,
      };
    case 'number[]':
      return {
        key,
        type: 'number',
        values: value.values.map((value) => value.value),
        operator,
      };
    case 'boolean[]':
      return {
        key,
        type: 'boolean',
        values: value.values.map((value) => value.value),
      };
  }
  throw invalidFilterError('metadata', false, value.type);
};

const toExactValues = (
  value: StringArrayFilterValue,
  isExact: boolean,
): string[] => {
  if (!isExact) {
    return value.values.map((value) => value.value);
  }
  return value.values.map((value) => `"${value.value}"`.replace(/""/g, '"'));
};

const fromFilter = (filter: QBFilter): WhereClause => {
  const exclude = isExclude(filter.operator);
  const type = toOperationTypeFromOperator(filter.operator);

  switch (filter.field) {
    case 'entity':
      if (
        filter.operator === OperatorIsPresent ||
        filter.operator === OperatorIsNotPresent
      ) {
        return {
          exclude,
          filter: {
            type,
            hasAspectFilter: {},
          },
        };
      }

      if (filter.value.type === 'string[]') {
        return {
          exclude,
          filter: {
            type,
            entityFilter: {
              entities: filter.value.values?.map((value) => ({
                entityID: value.value,
              })),
            },
          },
        };
      }
      throw invalidFilterError('entity', false, 'string');
    case 'entitySentiment':
      if (filter.value.type !== 'string') {
        throw invalidFilterError('entitySentiment', true, 'string');
      }
      return {
        exclude,
        filter: {
          type,
          entityFilter: {
            entities: [
              {
                entityID: filter.value.value ?? '',
                sentiment: toSentiment(filter.operator),
              },
            ],
          },
        },
      };
    case 'reason':
      if (
        filter.operator === OperatorIsPresent ||
        filter.operator === OperatorIsNotPresent
      ) {
        return {
          exclude,
          filter: {
            type,
            hasReasonFilter: {},
          },
        };
      }

      if (filter.value.type === 'string[]') {
        return {
          exclude,
          filter: {
            type,
            reasonFilter: {
              reasonIDs: filter.value.values?.map((value) => value.value),
            },
          },
        };
      }
      throw invalidFilterError('reason', false, 'string');
    case 'theme':
      if (filter.value.type !== 'string[]') {
        throw invalidFilterError('theme', false, 'string');
      }
      return {
        exclude,
        filter: {
          type,
          labelHierarchyFilter: {
            nodeIDs: filter.value.values?.map((value) => value.value),
          },
        },
      };
    case 'l1':
      if (filter.value.type !== 'string[]') {
        throw invalidFilterError('l1', false, 'string');
      }
      return {
        exclude,
        filter: {
          type,
          l1Filter: {
            l1IDs: filter.value.values?.map((value) => value.value),
          },
        },
      };
    case 'language':
      if (filter.value.type !== 'string[]') {
        throw invalidFilterError('language', false, 'string');
      }
      return {
        exclude,
        filter: {
          type,
          languageFilter: {
            iso6393s: filter.value.values?.map((value) => value.value),
          },
        },
      };
    case 'source':
      if (filter.value.type !== 'string[]') {
        throw invalidFilterError('source', false, 'string');
      }
      return {
        exclude,
        filter: {
          type,
          sourceFilter: {
            sources: filter.value.values?.map((value) =>
              decodeSourceFromString(value.value),
            ),
          },
        },
      };
    case 'sentiment':
    case 'reviewSentiment':
      if (filter.value.type !== 'string[]') {
        throw invalidFilterError('reviewSentiment', false, 'string');
      }
      return {
        exclude,
        filter: {
          type,
          reviewSentimentFilter: {
            sentimentIDs: filter.value.values?.map((value) => value.value),
          },
        },
      };
    case 'search':
      if (filter.value.type === 'string[]') {
        const isExact = checkIsExactSearchOperator(filter.operator);

        return {
          exclude,
          filter: {
            type,
            hybridSearchFilter: {
              query: '',
              queries: toExactValues(filter.value, isExact),
            },
          },
        };
      }
      if (filter.value.type !== 'string') {
        throw invalidFilterError('search', true, 'string');
      }
      return {
        exclude,
        filter: {
          hybridSearchFilter: {
            query: filter.value.value ?? '',
          },
        },
      };
    case 'feedback':
      return {
        exclude,
        filter: {
          type,
          hasUserContentFilter: true,
        },
      };

    case 'category': {
      if (filter.value.type !== 'string[]') {
        throw invalidFilterError('category', false, 'string');
      }
      return {
        exclude,
        filter: {
          categoryFilter: {
            categories: filter.value.values?.map((value) => value.value),
          },
        },
      };
    }

    case 'summary': {
      return {
        exclude,
        filter: {
          type,
          hasSummaryFilter: true,
        },
      };
    }

    default:
      if (
        filter.operator === OperatorIsPresent ||
        filter.operator === OperatorIsNotPresent
      ) {
        return {
          exclude,
          filter: {
            type,
            hasMetadataFilter: {
              keys: [filter.field],
            },
          },
        };
      }

      return {
        exclude,
        filter: {
          type,
          metadataFilter: toMetadataFilter(
            filter.field,
            filter.value,
            filter.operator,
          ),
        },
      };
  }
};

const toWhereClause = (node: QBNode): WhereClause => {
  if (node.type === 'filter') {
    return fromFilter(node);
  }

  return {
    op: {
      type: toOperationTypeFromConjunction(node.conjunction),
      params: node.children.map((child) => toWhereClause(child)),
    },
  };
};

export default toWhereClause;
